import { formatDateToYYYYMMDD, formatDatetime } from "react-lib/utils/dateUtils";

import { asDict, asNumber, asText } from "../patient/examModelV4";

/* ============================================================
  domain ล้วนของหน้า "รายการสั่งซื้อ"
  ข้อมูลจาก sequence V3 MeBilling.GET_ORDER_SUMMARY
  (state.MeBillingSequence.orderSummary, BIL/invoice-with-package-order)
   ============================================================ */

export const ORDER_SEQUENCE = "MeBilling" as const;
export const ORDER_ACTION = "GET_ORDER_SUMMARY" as const;
/** จำนวนต่อหน้าของ GET_ORDER_SUMMARY */
export const ORDER_LIMIT = 20;

export type OrderFilter = {
  search?: string;
  payment_channel?: string;
  status?: string;
  from_date?: string;
  to_date?: string;
};

export const CHANNEL_OPTIONS = [
  { value: "", text: "ทั้งหมด" },
  { value: "COUNTER", text: "ชำระหน้าร้าน" },
  { value: "2C2P", text: "2C2P" },
  { value: "SLIP", text: "ส่งสลิปโอนเงิน" },
];

export const STATUS_OPTIONS = [
  { value: "", text: "ทั้งหมด" },
  { value: "PAID", text: "ชำระแล้ว" },
  { value: "BILLED", text: "รอชำระ" },
];

const CHANNEL_NAMES: Record<string, string> = {
  COUNTER: "ชำระหน้าร้าน",
  "2C2P": "2C2P",
  SLIP: "ส่งสลิปโอนเงิน",
};

const STATUS_NAMES: Record<string, string> = {
  PAID: "ชำระแล้ว",
  BILLED: "รอชำระ",
  CANCELED: "ยกเลิก",
};

/** ตัวกรองที่ส่ง API: ตัดค่าว่างออก */
/** ตัดช่องว่าง และแปลงวันที่ DD/MM/YYYY (พ.ศ.) เป็น YYYY-MM-DD ที่ API ใช้ */
export const cleanFilter = (filter: OrderFilter): OrderFilter => {
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(filter)) {
    if (value === undefined || value === "") continue;
    result[key] = key === "from_date" || key === "to_date" ? formatDateToYYYYMMDD(value) : value;
  }
  return result as OrderFilter;
};

export type OrderItemRow = {
  id: number;
  code: string;
  name: string;
  quantity: number;
  unitPrice: number;
  total: number;
};

export type OrderRow = {
  id: number;
  code: string;
  customer: string;
  created: string;
  channels: string[];
  status: string;
  amount: number;
  paidAt: string;
  items: OrderItemRow[];
};

const dateTimeOf = (value: unknown): string => {
  const text = asText(value);
  return text ? formatDatetime(text) : "";
};

/** ใบแจ้งหนี้ -> แถว; ราคาต่อหน่วย = price_full / quantity ของรายการ */
export const orderRowsFromApi = (items: unknown): OrderRow[] =>
  (Array.isArray(items) ? items : [])
    .map(asDict)
    .filter((invoice) => asNumber(invoice.id) !== undefined)
    .map((invoice) => ({
      id: asNumber(invoice.id) as number,
      code: asText(invoice.code),
      customer: asText(invoice.patient_name),
      created: dateTimeOf(invoice.created),
      channels: (Array.isArray(invoice.payment_channels) ? invoice.payment_channels : []).map(
        (channel) => CHANNEL_NAMES[asText(channel)] ?? asText(channel),
      ),
      status: STATUS_NAMES[asText(invoice.status_name)] ?? asText(invoice.status_name),
      amount: asNumber(invoice.price) ?? 0,
      paidAt: dateTimeOf(invoice.paid_at),
      items: (Array.isArray(invoice.orders) ? invoice.orders : []).map(asDict).map((order) => {
        const quantity = asNumber(order.quantity) ?? 0;
        const total = asNumber(order.price_full) ?? 0;
        return {
          id: asNumber(order.id) ?? 0,
          code: asText(order.product_code),
          name: asText(order.product_name),
          quantity,
          unitPrice: quantity > 0 ? total / quantity : 0,
          total,
        };
      }),
    }));

export const sumAmount = (rows: OrderRow[]): number =>
  rows.reduce((sum, row) => sum + row.amount, 0);
